import React, {useCallback, useEffect, useRef} from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import {useSearchParams} from "react-router-dom";
import Alert, {AlertProps} from "@src/common/alert";
import {GenerativeModel, GenerativeModelVariation, useGenerativeModelAPI} from "../../api/generative_model_api";
import {GenerativeModelTestScenarioDTO, useGenerativeModelTestScenarioAPI} from "../../api/vault_test_scenario_api";
import cachedImageElement from "../../lib/cached-image-element";
import Lightbox from "../../common/lightbox";

const TestScenarios: React.FC = () => {
  const [queryParams] = useSearchParams();
  const [variations, setVariations] = React.useState<GenerativeModelVariation[]>([]);
  const [testScenarios, setTestScenarions] = React.useState<GenerativeModelTestScenarioDTO[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [selectedScenario, setSelectedScenario] = React.useState<GenerativeModelTestScenarioDTO | null>(null);
  const [processedImages, setProcessedImages] = React.useState<any[]>([]);
  const vaultAPI = useGenerativeModelTestScenarioAPI();
  const [lightboxImage, setLightboxImage] = React.useState<any | null>(null);
  var genModelAPI = useGenerativeModelAPI();
  const testScenariosRef = useRef<HTMLDivElement>(null);

  const scenarioColumns = [
    {
      name: "ID",
      cell: (row: GenerativeModelTestScenarioDTO) => <a target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BASE_URL}/vault-test/${row.id}`}>{row.id || ""}</a>,
      sortable: true,
      width: "150px"
    },
    {
      name: "Status",
      selector: (row: GenerativeModelTestScenarioDTO) => row.status || "",
      sortable: true,
      width: "150px"
    },
    {
      name: "Image Count",
      selector: (row: GenerativeModelTestScenarioDTO) => row.images?.length || "",
      sortable: true,
      width: "90px"
    },
    {
      name: "Created At",
      selector: (row: GenerativeModelTestScenarioDTO) => row.created_at || "",
      sortable: true,
      width: "150px"
    },
    {
      name: "Updated At",
      selector: (row: GenerativeModelTestScenarioDTO) => row.updated_at || "",
      sortable: true,
      width: "150px"
    },
    {
      name: "Actions",
      cell: (row: GenerativeModelTestScenarioDTO) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewImage(row)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            {selectedScenario?.id === row.id ? "Hide Images" : "View Images"}
          </button>
          <button
            onClick={() => handleResetTestScenario(row)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Reset
          </button>
          <button
            onClick={() => handleCancelTestScenario(row)}
            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Cancel Test
          </button>

          <button
            onClick={() => handleDeleteScenario(row.id)}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Delete
          </button>
        </div>
      ),
      width: "500px",
    }
  ];


  const handleCreateTestScenario = (vault: GenerativeModel) => {
    if (!vault.id) return;

    vaultAPI.createScenario(vault.id)
      .then(() => {
        setStatus({
          type: "success",
          message: "Test scenario created successfully"
        });
        fetchTestScenarios();
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message: "Failed to create test scenario: " + error.message
        });
      });
  }

  const handleViewImage = (scenario: GenerativeModelTestScenarioDTO) => {
    if (selectedScenario?.id === scenario.id) {
      setSelectedScenario(null);
      setProcessedImages([]);
    } else {
      setSelectedScenario(scenario);
      const images = scenario.images.map(image => ({
        ...image,
        media: cachedImageElement(image.image_path).src
      }));
      setProcessedImages(images);
    }
  }

  const handleResetTestScenario = (scenario: GenerativeModelTestScenarioDTO) => {
    if (scenario.id) {
      if (window.confirm("Are you sure you want to reset this test scenario? This action cannot be undone.")) {
        vaultAPI.resetEvaluation(scenario.id)
          .then(() => {
            setStatus({
              type: "success",
              message: "Test scenario reset successfully"
            });
            fetchTestScenarios();
          })
          .catch((error) => {
            setStatus({
              type: "error",
              message: "Failed to reset test scenario: " + error.message
            });
          });
      }
    }
  }

  const handleCancelTestScenario = (scenario: GenerativeModelTestScenarioDTO) => {
    if (scenario.id) {
      if (window.confirm("Are you sure you want to cancel this test scenario?")) {
        vaultAPI.cancelEvaluation(scenario.id)
          .then(() => {
            setStatus({
              type: "success",
              message: "Test scenario cancelled successfully"
            });
            fetchTestScenarios();
          })
          .catch((error) => {
            setStatus({
              type: "error",
              message: "Failed to cancel test scenario: " + error.message
            });
          });
      }
    }
  }
  const handleDeleteScenario = (id?: string) => {
    if (!id) return;

    if (window.confirm("Are you sure you want to delete this test scenario? This action cannot be undone.")) {
      vaultAPI.deleteScenario(id)
        .then(() => {
          setStatus({
            type: "success",
            message: "Test scenario deleted successfully"
          });
          fetchTestScenarios();
        })
        .catch((error) => {
          setStatus({
            type: "error",
            message: "Failed to delete test scenario: " + error.message
          });
        });
    }
  };

  const fetchTestScenarios = () => {
    if (!queryParams.get("generative_model_id")) {
      return;
    }
    setLoading(true);

    vaultAPI.listScenarios(queryParams.get("generative_model_id")!)
      .then((response) => {
        setTestScenarions(response);
        setLoading(false);
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message: "Failed to load test scenarios: " + error.message
        });
        setLoading(false);
      });
  };
  const fetchVaults = () => {
    if (!queryParams.get("generative_model_id")) {
      return;
    }
    setLoading(true);

    // @ts-ignore
    genModelAPI.getVariationsForModel(queryParams.get("generative_model_id"))
        .then((response: GenerativeModelVariation[]) => {
          setVariations(response);
          setLoading(false);
        })
        .catch((error: { message: string; }) => {
          setStatus({
            type: "error",
            message: "Failed to load vaults: " + error.message
          });
          setLoading(false);
        });
  };
  const getBaseModelForId = useCallback((variationId: string) => {
    for (let i = 0; i < variations.length; i++) {
      if (variations[i].id === variationId) {
        return variations[i].base_model
      }
    }
    return "unknown";
  }, [variations])

  useEffect(() => {
    fetchTestScenarios();
    fetchVaults()
  }, [queryParams]);

  return (
    <AppContainer>
      {/* <BackButton variant="ghost" onClick={() => navigate(`/vaults?user_id=${generativeModel?.created_by || ""}`)} /> */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Test Scenarios</h1>
          <button
            onClick={() => handleCreateTestScenario({ id: queryParams.get("generative_model_id")! } as GenerativeModel)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Create New Test Scenario
          </button>
        </div>

        {status && (
          <div className="mb-4">
            <Alert {...status} onClose={() => setStatus(null)} />
          </div>
        )}

        <div ref={testScenariosRef}>
          <DataTable
            columns={scenarioColumns}
            data={testScenarios}
            pagination
            progressPending={loading}
            progressComponent={<div className="py-6">Loading test scenarios...</div>}
            noDataComponent={<div className="py-6">No test scenarios found</div>}
          />
        </div>

        {selectedScenario && processedImages.length > 0 && (
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Images for Scenario: {selectedScenario.id}</h2>
              <button
                onClick={() => {
                  setSelectedScenario(null);
                  setProcessedImages([]);
                }}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Close Images
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processedImages.map((image, index) => (
                <div key={image.id} className="border rounded-lg p-4 bg-white shadow-md">
                  <div className="mb-3">
                    <img src={image.media} crossOrigin="anonymous" alt=""
                         className="w-full h-48 object-contain rounded-lg cursor-pointer"
                         onClick={() => setLightboxImage(image)}/>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm">Image score:</span>
                      <span className="text-sm">{image.score !== null && image.score !== undefined ? image.score : "Not rated"}</span>
                    </div>

                    {image.comment && (
                      <div>
                        <span className="font-semibold text-sm block mb-1">Comment:</span>
                        <p className="text-sm text-gray-600">{image.comment}</p>
                      </div>
                    )}

                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-500 space-y-1">
                        <div><strong>ID:</strong> {image.id}</div>
                        <div><strong>Generative Output ID:</strong> {image.generative_output_id}</div>
                        <div><strong>Variation ID:</strong> {image.generative_model_variation_id}</div>
                        <div><strong>Base Model:</strong> {getBaseModelForId(image.generative_model_variation_id)}
                        </div>
                        <div><strong>Prompt:</strong>{image.group_description}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
      {lightboxImage && <Lightbox imageUrl={lightboxImage.media || ""}
                                  onClose={() => setLightboxImage(null)}
                                  isOpen={true}/>}
    </AppContainer>
  );
};

export default TestScenarios;